import React, { useState } from "react";
import { MoreVertical, Trash2, Copy, Check } from "lucide-react";
import { FaPencil } from "react-icons/fa6";
import DateSeparator from "./DateSeparator";
import MediaItem from "./MediaItem";
import { formatDateTimeShort } from "utils/utilityFunctions";
import LinkPreview from "./LinkPreview";

const shouldShowDateSeparator = (currentMessage, previousMessage) => {
    if (!previousMessage) return true;
    const currentDate = new Date(currentMessage.createdAt);
    const previousDate = new Date(previousMessage.createdAt);
    return currentDate.toDateString() !== previousDate.toDateString();
};

const MessageBubble = ({
    msg,
    previousMessage,
    currentUser,
    menuOpenFor,
    setMenuOpenFor,
    onDelete,
    onUpdate,
    setReplyToMessage,
    setShowImagePreview,
    setSelectedMedia,
    teamMembers = [],
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedText, setEditedText] = useState(msg.text || "");
    const [copied, setCopied] = useState(false);

    const isCurrentUser = msg.senderId === currentUser?.user;
    const showDateSeparator = shouldShowDateSeparator(msg, previousMessage);

    const handleUpdate = async () => {
        await onUpdate(msg.id, editedText, msg.replyTo);
        setIsEditing(false);
    };

    const handleCopy = async () => {
        if (!msg?.text) return;
        try {
            await navigator.clipboard.writeText(msg.text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            // no-op; safe failure
            // eslint-disable-next-line no-console
            console.error("Copy failed:", e);
        }
    };

    const extractFirstUrl = (text) => {
        const match = text.match(/(https?:\/\/[^\s]+)/);
        return match ? match[0] : null;
    };

    const renderMessageText = (text) => {
        // First, split by URLs to make them clickable
        const urlPattern = /(https?:\/\/[^\s]+)/g;
        const parts = text.split(urlPattern);

        return parts.map((part, index) => {
            if (part.match(urlPattern)) {
                return (
                    <a
                        key={index}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline break-all"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {part}
                    </a>
                );
            }

            // For non-URL parts, process mentions
            if (!teamMembers || teamMembers.length === 0) return part;

            const validNames = teamMembers
                .filter((m) => m.first_name && m.last_name)
                .map((m) => `${m.first_name} ${m.last_name}`)
                .sort((a, b) => b.length - a.length);

            if (validNames.length === 0) return part;

            const escapedNames = validNames.map((name) =>
                name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
            );
            const mentionPattern = new RegExp(`(@(?:${escapedNames.join("|")}))\\b`, "g");
            const mentionParts = part.split(mentionPattern);

            return mentionParts.map((subPart, subIndex) => {
                if (subPart.startsWith("@")) {
                    const name = subPart.substring(1);
                    if (validNames.includes(name)) {
                        return (
                            <span key={`${index}-${subIndex}`} className="text-purple-600 font-medium">
                                {subPart}
                            </span>
                        );
                    }
                }
                return subPart;
            });
        });
    };

    const firstUrl = msg.text ? extractFirstUrl(msg.text) : null;

    return (
        <React.Fragment>
            {showDateSeparator && <DateSeparator date={msg.createdAt} />}
            <div
                className={`mb-2 flex ${isCurrentUser ? "justify-end" : "justify-start"
                    }`}
            >
                {isCurrentUser && (
                    <div className="relative">
                        <button
                            className="text-gray-600 mt-3"
                            onClick={() =>
                                setMenuOpenFor((prev) => (prev === msg.id ? null : msg.id))
                            }
                        >
                            <MoreVertical size={20} />
                        </button>
                        {menuOpenFor === msg.id && (
                            <div className="absolute right-4 top-2 mt-2 w-48 bg-white rounded-lg shadow-md z-20 border border-gray-200 overflow-hidden">
                                <button
                                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-100 flex items-center gap-3"
                                    onClick={async () => {
                                        await handleCopy();
                                        setMenuOpenFor(null);
                                    }}
                                    aria-label="Copy Message"
                                >
                                    <Copy size={18} className="text-gray-600" />
                                    <span>{copied ? "Copied" : "Copy"}</span>
                                </button>
                                <button
                                    className="w-full text-left px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-3 border-t border-gray-100"
                                    onClick={() => {
                                        setMenuOpenFor(null);
                                        onDelete(msg.id);
                                    }}
                                    aria-label="Delete Message"
                                >
                                    <Trash2 size={18} className="text-red-500" />
                                    <span>Delete</span>
                                </button>
                                <button
                                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-100 flex items-center gap-3 border-t border-gray-100"
                                    onClick={() => {
                                        setIsEditing(true);
                                        setEditedText(msg.text);
                                        setMenuOpenFor(null);
                                    }}
                                >
                                    <FaPencil /> Edit
                                </button>
                            </div>
                        )}
                    </div>
                )}
                <div
                    className={`max-w-xs md:max-w-md lg:max-w-lg rounded-lg px-3 pt-3 pb-1 ${isCurrentUser
                        ? "bg-purple-100 text-black"
                        : "bg-gray-100 text-gray-900"
                        }`}
                >
                    {
                        (previousMessage?.senderId !== msg.senderId || showDateSeparator || msg.replyTo) && (
                            <div className="font-semibold text-xs mb-1">
                                {msg.senderName} • {formatDateTimeShort(msg.createdAt)}
                            </div>
                        )
                    }
                    {msg.has_media && (
                        <div className="mb-2 flex flex-row flex-wrap gap-2">
                            {(Array.isArray(msg.media) ? msg.media : [msg.media]).map(
                                (mediaItem, idx) => (
                                    <MediaItem
                                        key={mediaItem.mediaId || idx}
                                        mediaItem={mediaItem}
                                        setShowImagePreview={setShowImagePreview}
                                        setSelectedMedia={setSelectedMedia}
                                    />
                                )
                            )}
                        </div>
                    )}
                    <>
                        {msg.replyTo && (
                            <div className="mb-2 px-2 py-1 border-l-4 border-purple-400 bg-purple-50 text-xs text-gray-700 rounded">
                                <div className="font-medium">{msg.replyTo.senderName}</div>
                                <div>
                                    {msg.replyTo.text
                                        ? msg.replyTo.text
                                        : msg.replyTo.has_media
                                            ? "Media message"
                                            : ""}
                                </div>
                            </div>
                        )}
                        {msg.text && (
                            <div
                                className="text-sm whitespace-pre-wrap break-words"
                                style={{ userSelect: "text" }}
                            >
                                {renderMessageText(msg.text)}
                                {firstUrl && <LinkPreview url={firstUrl} />}
                            </div>
                        )}
                    </>
                    {!isCurrentUser && (
                        <button
                            onClick={() => setReplyToMessage(msg)}
                            className="text-sm text-purple-900 mt-2 hover:font-bold"
                        >
                            Reply
                        </button>
                    )}
                    {isCurrentUser && (
                        <div className="text-[10px] text-gray-500 text-right mt-1">
                            Delivered
                        </div>
                    )}
                </div>
            </div>
            {isEditing && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    onClick={() => setIsEditing(false)}
                >
                    <div
                        className="w-full max-w-2xl bg-white rounded-xl shadow-xl p-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="text-sm font-semibold mb-2">Edit Message</div>
                        <textarea
                            className="w-full h-56 md:h-72 border rounded-lg p-3 text-sm resize-none"
                            value={editedText}
                            onChange={(e) => setEditedText(e.target.value)}
                            rows={8}
                        />
                        <div className="flex gap-2 justify-end mt-3">
                            <button
                                onClick={handleUpdate}
                                className="px-3 py-2 bg-purple-700 text-white text-sm rounded hover:bg-purple-800"
                            >
                                Save
                            </button>
                            <button
                                onClick={() => setIsEditing(false)}
                                className="px-3 py-2 border text-sm rounded hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default MessageBubble;
